import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  Paper,
  TextField,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import { transceive } from './actions';
import { stringToHex } from './backupService';
import {
  checkAllOpenPGPKeys,
  getOpenPGPKeyStatistics,
  getRecommendedAction,
  OPENPGP_KEY_TYPES
} from './openpgpKeyDetection';
import OpenPGPKeyStatusCard from './OpenPGPKeyStatusCard';
import OverwriteConfirmationDialog from './OverwriteConfirmationDialog';

// Key algorithms for OpenPGP
const OPENPGP_ALGORITHMS = [
  { value: 'rsa2048', label: 'RSA-2048', description: 'Most compatible, good security' },
  { value: 'rsa3072', label: 'RSA-3072', description: 'High security (requires firmware support)' },
  { value: 'rsa4096', label: 'RSA-4096', description: 'Maximum RSA security (slower)' },
  { value: 'ed25519', label: 'Ed25519', description: 'Modern, fast, excellent security' },
  { value: 'cv25519', label: 'Curve25519', description: 'For encryption (X25519)' },
  { value: 'nistp256', label: 'NIST P-256', description: 'Standard elliptic curve' },
  { value: 'nistp384', label: 'NIST P-384', description: 'High security elliptic curve' }
];

const OpenPGPKeySetupWizard = ({ open, onClose, dispatch }) => {
  const [activeStep, setActiveStep] = useState(0);

  // Status detection states
  const [keyStatuses, setKeyStatuses] = useState([]);
  const [keyStats, setKeyStats] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState('');

  // Configuration states
  const [keyConfigs, setKeyConfigs] = useState({
    'sig': { mode: 'generate', algorithm: 'rsa2048' },
    'dec': { mode: 'generate', algorithm: 'rsa2048' },
    'aut': { mode: 'generate', algorithm: 'rsa2048' }
  });

  // Overwrite confirmation
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingChange, setPendingChange] = useState(null);

  const [adminPin, setAdminPin] = useState('');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');

  const steps = ['Welcome & Status Check', 'Configure Keys', 'Authentication', 'Generate/Import', 'Complete'];

  // Detect key status when wizard opens
  useEffect(() => {
    if (open) {
      detectKeyStatus();
    }
  }, [open]);

  const detectKeyStatus = async () => {
    setStatusLoading(true);
    setStatusError('');

    try {
      // Check all OpenPGP keys
      const statuses = await checkAllOpenPGPKeys((apdu) => dispatch(transceive(apdu)));
      setKeyStatuses(statuses);

      // Calculate statistics
      const stats = getOpenPGPKeyStatistics(statuses);
      setKeyStats(stats);

      // Set intelligent defaults based on status
      const defaultConfigs = {};
      statuses.forEach((status) => {
        defaultConfigs[status.keyType] = {
          mode: getRecommendedAction(status.status),
          algorithm: 'rsa2048' // RSA-2048 default
        };
      });
      setKeyConfigs(defaultConfigs);

    } catch (err) {
      console.error('Failed to detect key status:', err);
      setStatusError(err.message || 'Failed to check key status');

      // Set default empty status on error
      const emptyStatuses = OPENPGP_KEY_TYPES.map(keyType => ({
        keyType: keyType.id,
        status: 'error',
        error: 'Detection failed',
        hasKey: false
      }));
      setKeyStatuses(emptyStatuses);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleKeyConfigChange = (keyId, field, value) => {
    // Check if this is a mode change that requires overwrite confirmation
    if (field === 'mode' && value !== 'skip') {
      const keyStatus = keyStatuses.find(k => k.keyType === keyId);

      // If key is configured and user wants to generate/import, show confirmation
      if (keyStatus && keyStatus.status === 'configured') {
        setPendingChange({ keyId, field, value });
        setConfirmOpen(true);
        return;
      }
    }

    // Apply change directly
    setKeyConfigs((prev) => ({
      ...prev,
      [keyId]: { ...prev[keyId], [field]: value }
    }));
  };

  const handleOverwriteConfirm = () => {
    if (pendingChange) {
      setKeyConfigs((prev) => ({
        ...prev,
        [pendingChange.keyId]: {
          ...prev[pendingChange.keyId],
          [pendingChange.field]: pendingChange.value
        }
      }));
      setPendingChange(null);
    }
    setConfirmOpen(false);
  };

  const handleOverwriteCancel = () => {
    setPendingChange(null);
    setConfirmOpen(false);
  };

  const handleReset = () => {
    setActiveStep(0);
    setKeyConfigs({
      'sig': { mode: 'generate', algorithm: 'rsa2048' },
      'dec': { mode: 'generate', algorithm: 'rsa2048' },
      'aut': { mode: 'generate', algorithm: 'rsa2048' }
    });
    setAdminPin('');
    setProcessing(false);
    setProgress(0);
    setResults([]);
    setError('');
  };

  const handleExecute = async () => {
    setProcessing(true);
    setError('');
    setResults([]);
    setProgress(0);

    try {
      // Select OpenPGP applet
      await dispatch(transceive('00A4040006D27600012401'));

      // Verify Admin PIN (PW3)
      if (adminPin) {
        const pinHex = stringToHex(adminPin);
        const verifyAPDU = `00200083${(pinHex.length / 2).toString(16).padStart(2, '0')}${pinHex}`;
        const verifyRes = await dispatch(transceive(verifyAPDU));

        if (!verifyRes.endsWith('9000')) {
          throw new Error('Admin PIN verification failed. Please check your PIN.');
        }
      }

      const totalKeys = Object.values(keyConfigs).filter(c => c.mode !== 'skip').length;
      let completed = 0;

      // Process each key
      for (const keyType of OPENPGP_KEY_TYPES) {
        const config = keyConfigs[keyType.id];

        if (config.mode === 'skip') {
          continue;
        }

        try {
          if (config.mode === 'generate') {
            // Generate key on card using GENERATE_ASYMMETRIC_KEY_PAIR (0x47)
            // This is a simplified version - actual implementation requires proper key attributes
            const keySlot = keyType.id === 'sig' ? '01' : keyType.id === 'dec' ? '02' : '03';
            const genAPDU = `004700${keySlot}00`;
            const response = await dispatch(transceive(genAPDU));

            if (response.endsWith('9000')) {
              setResults((prev) => [...prev, {
                key: keyType.id,
                name: keyType.name,
                success: true,
                message: 'Key generated successfully'
              }]);
            } else {
              throw new Error(`Generation failed with status: ${response.slice(-4)}`);
            }
          }
          // For 'import' mode, we just show instructions in the UI
          // Actual import would require user to provide key material

          completed++;
          setProgress((completed / totalKeys) * 100);
        } catch (err) {
          setResults((prev) => [...prev, {
            key: keyType.id,
            name: keyType.name,
            success: false,
            message: err.message
          }]);
        }
      }

      setActiveStep(4); // Move to complete step
    } catch (err) {
      setError(err.message || 'An error occurred during key setup');
    } finally {
      setProcessing(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Welcome & Status Check
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Welcome to OpenPGP Key Setup Wizard
            </Typography>
            <Typography variant="body1" paragraph>
              This wizard will help you configure OpenPGP cryptographic keys on your CanoKey device.
            </Typography>

            <Alert severity="info" style={{ marginBottom: 16 }}>
              <AlertTitle>What is OpenPGP?</AlertTitle>
              OpenPGP is an open standard for email encryption and digital signatures. It's widely used for secure communication
              and is compatible with tools like GnuPG, Thunderbird, and many others.
            </Alert>

            {/* Status Detection */}
            {statusLoading && (
              <Box style={{ textAlign: 'center', padding: 32 }}>
                <CircularProgress />
                <Typography variant="body2" style={{ marginTop: 16 }}>
                  Checking key status...
                </Typography>
              </Box>
            )}

            {statusError && (
              <Alert severity="warning" style={{ marginBottom: 16 }}>
                <AlertTitle>Status Check Failed</AlertTitle>
                {statusError}. The wizard will continue with default settings.
              </Alert>
            )}

            {!statusLoading && keyStats && (
              <Paper elevation={1} style={{ padding: 16, marginBottom: 16 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Device Status Summary:
                </Typography>
                <Box style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                  <Chip
                    label={`${keyStats.empty} Empty`}
                    color="default"
                    size="small"
                  />
                  <Chip
                    label={`${keyStats.configured} Configured`}
                    color="primary"
                    size="small"
                  />
                  {keyStats.errors > 0 && (
                    <Chip
                      label={`${keyStats.errors} Errors`}
                      color="secondary"
                      size="small"
                    />
                  )}
                </Box>
              </Paper>
            )}

            <Paper elevation={1} style={{ padding: 16, marginBottom: 16 }}>
              <Typography variant="subtitle2" gutterBottom>Available Key Types:</Typography>
              {OPENPGP_KEY_TYPES.map((keyType) => (
                <Box key={keyType.id} style={{ marginBottom: 8 }}>
                  <Typography variant="body2">
                    <strong>{keyType.name}:</strong> {keyType.description}
                  </Typography>
                </Box>
              ))}
            </Paper>

            <Alert severity="warning">
              <AlertTitle>Important: Key Backup Strategy</AlertTitle>
              <Typography variant="body2" paragraph>
                You have two options for key management:
              </Typography>
              <Typography variant="body2" component="div">
                <strong>1. Generate on Card (Recommended):</strong>
                <ul>
                  <li>Keys generated inside the secure element</li>
                  <li>Maximum security - keys never leave the device</li>
                  <li>⚠️ Cannot be backed up or transferred to another device</li>
                </ul>
              </Typography>
              <Typography variant="body2" component="div">
                <strong>2. Import External Keys:</strong>
                <ul>
                  <li>Keys generated on your computer using GnuPG or other tools</li>
                  <li>Can be backed up and restored to replacement device if lost</li>
                  <li>⚠️ Requires secure backup management (encrypted storage)</li>
                </ul>
              </Typography>
            </Alert>
          </Box>
        );

      case 1: // Configure Keys
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Configure OpenPGP Keys
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Review the current status of each key type and configure your desired actions.
            </Typography>

            {keyStats && (
              <Alert
                severity={keyStats.empty === keyStats.total ? 'info' : 'warning'}
                style={{ marginBottom: 16 }}
              >
                <AlertTitle>
                  {keyStats.empty === keyStats.total
                    ? 'All Keys Empty - Ready for Configuration'
                    : 'Some Keys Already Configured'}
                </AlertTitle>
                {keyStats.empty === keyStats.total ? (
                  <Typography variant="body2">
                    Your device has no existing OpenPGP keys. You can safely configure all key types.
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    {keyStats.configured} key(s) already configured.
                    Replacing them will permanently delete the existing keys and make encrypted data inaccessible.
                    Make sure you have backups if needed.
                  </Typography>
                )}
              </Alert>
            )}

            {OPENPGP_KEY_TYPES.map((keyType) => {
              const status = keyStatuses.find(k => k.keyType === keyType.id) || {
                keyType: keyType.id,
                status: 'empty',
                hasKey: false
              };
              const config = keyConfigs[keyType.id] || { mode: 'generate', algorithm: 'rsa2048' };

              return (
                <OpenPGPKeyStatusCard
                  key={keyType.id}
                  keyType={keyType}
                  status={status}
                  config={config}
                  onConfigChange={handleKeyConfigChange}
                />
              );
            })}
          </Box>
        );

      case 2: // Authentication
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Authentication Required
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Enter your OpenPGP Admin PIN to proceed with key operations.
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Default Credentials</AlertTitle>
              <Typography variant="body2">
                <strong>Default Admin PIN (PW3):</strong> 12345678<br />
                <strong>Note:</strong> The Admin PIN is different from the User PIN (PW1)
              </Typography>
            </Alert>

            <TextField
              fullWidth
              label="Admin PIN (PW3)"
              type="password"
              value={adminPin}
              onChange={(e) => setAdminPin(e.target.value)}
              placeholder="Default: 12345678"
              helperText="8-digit Admin PIN for key management operations"
            />
          </Box>
        );

      case 3: // Generate/Import
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {processing ? 'Processing...' : 'Ready to Execute'}
            </Typography>

            {!processing && (
              <>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Review your configuration and click "Execute" to begin key setup.
                </Typography>

                <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Configuration Summary:</Typography>
                  {OPENPGP_KEY_TYPES.map((keyType) => {
                    const config = keyConfigs[keyType.id];
                    if (config.mode === 'skip') return null;

                    const alg = OPENPGP_ALGORITHMS.find(a => a.value === config.algorithm);
                    return (
                      <Box key={keyType.id} sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          <strong>{keyType.name}:</strong> {config.mode === 'generate' ? 'Generate' : 'Import'} {alg?.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Paper>

                {Object.values(keyConfigs).some(c => c.mode === 'import') && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>External Key Import Instructions</AlertTitle>
                    <Typography variant="body2" paragraph>
                      For keys configured to import external keys, you'll need to generate keys using GnuPG first.
                    </Typography>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2">View Key Generation Commands</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
{`# Generate a new OpenPGP key pair with GnuPG
gpg --full-generate-key

# Or generate with specific algorithm
gpg --quick-generate-key "Your Name <email@example.com>" rsa2048 sign,auth,encr

# Export private key for backup (keep this secure!)
gpg --export-secret-keys --armor your-key-id > private-key.asc

# Import to CanoKey (after generating on card, use keytocard)
gpg --edit-key your-key-id
> keytocard

# For Ed25519 keys
gpg --quick-generate-key "Your Name <email@example.com>" ed25519 sign
gpg --quick-add-key <fingerprint> cv25519 encr
gpg --quick-add-key <fingerprint> ed25519 auth`}
                        </Typography>
                        <Alert severity="info" sx={{ mt: 1 }}>
                          After generating keys externally, use GnuPG's "keytocard" command to import them to your CanoKey.
                        </Alert>
                      </AccordionDetails>
                    </Accordion>
                  </Alert>
                )}
              </>
            )}

            {processing && (
              <Box>
                <LinearProgress variant="determinate" value={progress} sx={{ mb: 2 }} />
                <Typography variant="body2" align="center">
                  {Math.round(progress)}% complete
                </Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>Error</AlertTitle>
                {error}
              </Alert>
            )}

            {results.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Results:</Typography>
                {results.map((result, idx) => (
                  <Alert
                    key={idx}
                    severity={result.success ? 'success' : 'error'}
                    icon={result.success ? <CheckCircleIcon /> : <WarningIcon />}
                    sx={{ mb: 1 }}
                  >
                    <strong>{result.name}:</strong> {result.message}
                  </Alert>
                ))}
              </Box>
            )}
          </Box>
        );

      case 4: // Complete
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" />
              Setup Complete
            </Typography>

            <Typography variant="body1" paragraph>
              Your OpenPGP key setup has been completed.
            </Typography>

            {results.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Summary:</Typography>
                {results.map((result, idx) => (
                  <Alert
                    key={idx}
                    severity={result.success ? 'success' : 'warning'}
                    sx={{ mb: 1 }}
                  >
                    <strong>{result.name}:</strong> {result.message}
                  </Alert>
                ))}
              </Box>
            )}

            <Alert severity="info">
              <AlertTitle>Next Steps</AlertTitle>
              <Typography variant="body2">
                • For card-generated keys: Your keys are ready to use with GnuPG<br />
                • For imported keys: Use GnuPG's "keytocard" command to import your external keys<br />
                • Configure your email client (Thunderbird, etc.) to use your OpenPGP card<br />
                • Set up your User PIN (PW1) for daily operations<br />
                • Remember to backup your external keys securely if you chose the import option
              </Typography>
            </Alert>

            {Object.values(keyConfigs).some(c => c.mode === 'import') && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>Important: Backup Your External Keys</AlertTitle>
                <Typography variant="body2">
                  If you chose to import external keys, make sure to store the private key files in a secure, encrypted location
                  (password manager, encrypted USB drive, etc.). Without these backups, you cannot recover your keys if the device is lost.
                </Typography>
              </Alert>
            )}

            <Alert severity="success" sx={{ mt: 2 }}>
              <AlertTitle>Using Your OpenPGP Card</AlertTitle>
              <Typography variant="body2">
                To use your CanoKey with GnuPG, run:<br />
                <code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>
                  gpg --card-status
                </code>
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={processing}
      >
        <DialogTitle>
          OpenPGP Key Setup Wizard
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} style={{ marginBottom: 24, marginTop: 8 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent()}
        </DialogContent>
        <DialogActions>
          {activeStep === 0 && (
            <>
              <Button onClick={onClose} disabled={processing || statusLoading}>
                Cancel
              </Button>
              <Button onClick={handleNext} variant="contained" disabled={statusLoading}>
                Next
              </Button>
            </>
          )}

          {activeStep > 0 && activeStep < 3 && (
            <>
              <Button onClick={handleBack} disabled={processing}>
                Back
              </Button>
              <Button onClick={onClose} disabled={processing}>
                Cancel
              </Button>
              <Button onClick={handleNext} variant="contained" disabled={processing}>
                Next
              </Button>
            </>
          )}

          {activeStep === 3 && (
            <>
              <Button onClick={handleBack} disabled={processing}>
                Back
              </Button>
              <Button onClick={onClose} disabled={processing}>
                Cancel
              </Button>
              <Button
                onClick={handleExecute}
                variant="contained"
                disabled={processing || !adminPin}
              >
                Execute
              </Button>
            </>
          )}

          {activeStep === 4 && (
            <>
              <Button onClick={handleReset}>
                Setup Another Configuration
              </Button>
              <Button onClick={onClose} variant="contained">
                Close
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Overwrite Confirmation Dialog */}
      {pendingChange && (
        <OverwriteConfirmationDialog
          open={confirmOpen}
          onClose={handleOverwriteCancel}
          onConfirm={handleOverwriteConfirm}
          slotInfo={{
            ...OPENPGP_KEY_TYPES.find(k => k.id === pendingChange.keyId),
            ...keyStatuses.find(k => k.keyType === pendingChange.keyId)
          }}
          type="openpgp"
        />
      )}
    </>
  );
};

export default connect()(OpenPGPKeySetupWizard);
